// frontend/src/hooks/useTankPhotos.ts
/**
 * React Query hooks for a tank's shots (docs/plans/2026-10-28-tank-shots-implementation.md,
 * 3.3 and 4). Every change refreshes the tank's shots (`tankPhotoKeys.all`) and the
 * tanks list, whose cover and "Tank shots · N" follow the shots.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { tankPhotosApi } from '@/api/tankPhotos';
import { tanksQueryKey } from '@/api/tanks';
import { tankPhotoKeys } from './tankPhotoKeys';
import { shareLinkKeys } from './shareLinkKeys';
import type {
  TankCoverResponse,
  TankPhoto,
  TankPhotoFraming,
  TankPhotoIndex,
  TankPhotoPage,
  TankPhotoUpdate,
} from '@/types/tankPhotos';

export interface TankPhotoEdit {
  id: number;
  data: TankPhotoUpdate;
}

/** A framing to save, or null to reset the shot's line up. */
export interface TankPhotoFramingChange {
  id: number;
  framing: TankPhotoFraming | null;
}

export const TANK_PHOTO_PAGE_SIZE = 60;

/**
 * Signed URLs outlive this by far; a refetch after it hands out fresh ones before
 * the old ones expire. A thumbnail that fails to load refetches sooner.
 */
const TANK_PHOTO_STALE_MS = 30 * 60 * 1000;

/**
 * A FastAPI 422 carries a list as its detail, which the api client turns into
 * "[object Object]"; say something a person can act on instead.
 */
function messageOf(error: Error, fallback: string): string {
  return !error.message || error.message.startsWith('[object') ? fallback : error.message;
}

function invalidateTankShots(queryClient: QueryClient, tankId: string): void {
  void queryClient.invalidateQueries({ queryKey: tankPhotoKeys.all(tankId) });
  void queryClient.invalidateQueries({ queryKey: tanksQueryKey });
}

/** The tank's shots, newest first, 60 at a time. */
export function useTankPhotoList(
  tankId: string | undefined
): UseInfiniteQueryResult<InfiniteData<TankPhotoPage, string | null>, Error> {
  return useInfiniteQuery({
    queryKey: tankPhotoKeys.list(tankId ?? ''),
    queryFn: ({ pageParam }) =>
      tankPhotosApi.list(tankId as string, { after: pageParam, limit: TANK_PHOTO_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_after,
    enabled: !!tankId,
    staleTime: TANK_PHOTO_STALE_MS,
  });
}

/** Every shot as (id, date, thumb), oldest first. */
export function useTankPhotoIndex(
  tankId: string | undefined
): UseQueryResult<TankPhotoIndex, Error> {
  return useQuery({
    queryKey: tankPhotoKeys.index(tankId ?? ''),
    queryFn: () => tankPhotosApi.index(tankId as string),
    enabled: !!tankId,
    staleTime: TANK_PHOTO_STALE_MS,
  });
}

export function useTankPhoto(
  tankId: string | undefined,
  id: number | undefined
): UseQueryResult<TankPhoto, Error> {
  return useQuery({
    queryKey: tankPhotoKeys.one(tankId ?? '', id ?? 0),
    queryFn: () => tankPhotosApi.get(tankId as string, id as number),
    enabled: !!tankId && id !== undefined,
    staleTime: TANK_PHOTO_STALE_MS,
  });
}

export function useEditTankPhoto(
  tankId: string
): UseMutationResult<TankPhoto, Error, TankPhotoEdit> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: TankPhotoEdit) => tankPhotosApi.update(tankId, id, data),
    onSuccess: () => {
      invalidateTankShots(queryClient, tankId);
      notifications.show({ message: 'Tank shot updated', color: 'teal' });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Tank shot wasn't updated",
        message: messageOf(error, 'Check the date: it can be from 1990 up to today.'),
        color: 'red',
      });
    },
  });
}

export function useDeleteTankPhoto(tankId: string): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tankPhotosApi.delete(tankId, id),
    onSuccess: () => {
      invalidateTankShots(queryClient, tankId);
      // A link that showed the shot now has a removed photo.
      void queryClient.invalidateQueries({ queryKey: shareLinkKeys.tank(tankId) });
      notifications.show({ message: 'Tank shot deleted', color: 'teal' });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Tank shot wasn't deleted",
        message: messageOf(error, 'Try again in a moment.'),
        color: 'red',
      });
    },
  });
}

/** An id makes that shot the cover; null goes back to the latest shot. */
export function useSetTankCover(
  tankId: string
): UseMutationResult<TankCoverResponse, Error, number | null> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tankPhotoId: number | null) => tankPhotosApi.setCover(tankId, tankPhotoId),
    onSuccess: (_result, tankPhotoId) => {
      invalidateTankShots(queryClient, tankId);
      notifications.show({
        message: tankPhotoId === null ? 'Cover is the latest shot' : 'Cover photo set',
        color: 'teal',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Cover wasn't changed",
        message: messageOf(error, 'Try again in a moment.'),
        color: 'red',
      });
    },
  });
}

/**
 * Line a shot up (a framing) or reset it (null). The shots and the tanks list
 * refresh: the compare views and the Line up ghost (the cover's framing) follow.
 */
export function useSetTankPhotoFraming(
  tankId: string
): UseMutationResult<TankPhoto, Error, TankPhotoFramingChange> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, framing }: TankPhotoFramingChange) =>
      framing
        ? tankPhotosApi.setFraming(tankId, id, framing)
        : tankPhotosApi.clearFraming(tankId, id),
    onSuccess: (_shot, { framing }) => {
      invalidateTankShots(queryClient, tankId);
      notifications.show({ message: framing ? 'Lined up' : 'Line up reset', color: 'teal' });
    },
    onError: (error: Error, { framing }) => {
      notifications.show({
        title: framing ? "Line up wasn't saved" : "Line up wasn't reset",
        message: messageOf(error, 'Try again in a moment.'),
        color: 'red',
      });
    },
  });
}
